import { Text } from "@overmap-ai/blocks"
import { type ProcedureStepComment, type Stored, UNLABELLED_ASSET_LABEL } from "@overmap-ai/common"
import { selectAssetById, selectProcedureById, selectProcedureStepById, selectUserById } from "@overmap-ai/core"
import { Link } from "@tanstack/react-router"
import { memo } from "react"

import { interactiveTextCva } from "../../cva"
import { ProjectTimelineItemType } from "../../enums/projects"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { ProcedureTypeIcon } from "../Icons/ProcedureTypeIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { ProcedureIndexLabel } from "../IndexLabels/ProcedureIndexLabel"
import { ProjectTimelineCard } from "./ProjectTimelineCard"
import { getTimelineDate } from "./utils"

interface ProjectTimelineProcedureStepCommentItemProps {
	procedureStepComment: Stored<ProcedureStepComment>
}

export const ProjectTimelineProcedureStepCommentItem = memo<ProjectTimelineProcedureStepCommentItemProps>((props) => {
	const { procedureStepComment } = props

	const user = useAppSelector(selectUserById(procedureStepComment.created_by ?? -1))
	const procedureStep = useAppSelector(selectProcedureStepById(procedureStepComment.procedure_step))
	const procedure = useAppSelector(selectProcedureById(procedureStepComment.procedure))!
	const asset = useAppSelector(selectAssetById(procedure?.asset ?? ""))!

	return (
		<ProjectTimelineCard type={ProjectTimelineItemType.PROCEDURE_STEP_COMMENT}>
			<Text size="sm" accentColor="base" className="min-w-0">
				<Text highContrast>{getUserDisplayLabel(user)}</Text>
				&nbsp;commented on procedure step&nbsp;
				<Text highContrast>{procedureStep?.name ?? "deleted step"}</Text>
				<Text accentColor="base">
					&nbsp;&#8226;&nbsp;{formatCompactDate(getTimelineDate(procedureStepComment))}
				</Text>
			</Text>

			<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
				<Text size="sm" accentColor="base" className="min-w-0">
					Context:&nbsp;
					<Link
						from="/$organizationId/$projectId"
						to="/$organizationId/$projectId/procedures/$procedureId"
						params={{ procedureId: procedure.uuid }}
					>
						<Text highContrast className={interactiveTextCva()}>
							<ProcedureTypeIcon className="align-sub" procedureTypeId={procedure.procedure_type} />
							&nbsp;
							<ProcedureIndexLabel procedureId={procedure.uuid} highContrast />
						</Text>
					</Link>
				</Text>

				<Text size="sm" accentColor="base" className="min-w-0">
					in&nbsp;
					<Link
						from="/$organizationId/$projectId"
						to="/$organizationId/$projectId/assets/$assetId"
						params={{ assetId: asset.uuid }}
					>
						<AssetTypeIcon className="align-sub" assetTypeId={asset.asset_type} />
						&nbsp;
						<Text className={interactiveTextCva()} accentColor="base">
							<AssetIndexLabel assetId={asset.uuid} highContrast />
							&nbsp;
							<Text>{asset.label || UNLABELLED_ASSET_LABEL}</Text>
						</Text>
					</Link>
				</Text>
			</div>
		</ProjectTimelineCard>
	)
})
ProjectTimelineProcedureStepCommentItem.displayName = "ProjectTimelineProcedureStepCommentItem"
